/**
 * BulkWhatsappModal — semi-manual bulk WhatsApp queue.
 *
 * V1: no WhatsApp Business API, no automated sending. The operator walks
 * through the selected leads one by one. "Abrir WhatsApp" opens the wa.me
 * deep link and marks the lead as `opened` but does NOT advance — the
 * operator confirms manually with "Marcar enviado" or "Saltar". A snapshot
 * of the queue is persisted in localStorage so switching to the WhatsApp
 * app on mobile and coming back keeps the operator in the same slot even
 * after a page reload.
 *
 * PR #104 — Fuente principal ahora es el catálogo backend
 * `message_templates` (canal whatsapp). Cuando el backend responde y hay
 * plantillas, el selector, la default recomendada por fase y el
 * `template_key` que se persiste en messages_log salen del backend. Si el
 * fetch falla o devuelve vacío, el modal cae a whatsappTemplates.js /
 * leadPlaybook con banner claro y los controles legacy (overrides
 * localStorage, custom management) reaparecen sólo en ese modo.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  X, WhatsappLogo, Copy, CheckCircle, ArrowLeft, ArrowRight,
  Check, SkipForward, ClipboardText, ArrowClockwise, ArrowCounterClockwise,
  FloppyDisk, Trash,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import {
  buildWhatsappUrl,
  getLeadPhone,
  normalizeWhatsappPhone,
  isValidWhatsappPhone,
  renderWhatsappTemplate,
  whatsappTemplateVariables,
  BULK_TEMPLATE_KEY,
  loadQueueSnapshot,
  saveQueueSnapshot,
  clearQueueSnapshot,
  sameLeadSet,
} from "@/lib/bulkWhatsapp";
import {
  WHATSAPP_TEMPLATES,
  DEFAULT_TEMPLATE_KEY,
  MAX_CUSTOM_TEMPLATES,
  getTemplateBody,
  loadCustomTemplates,
  saveCustomTemplate,
  deleteCustomTemplate,
  loadLastEditor,
  saveLastEditor,
  isBuiltInTemplate,
  hasBuiltInOverride,
  saveBuiltInOverride,
  deleteBuiltInOverride,
} from "@/lib/whatsappTemplates";
import { normalizeStatus, statusLabel } from "@/lib/leadStatuses";
import { useMessageTemplates } from "@/hooks/useMessageTemplates";
import {
  pickDefaultTemplateForStage,
  orderTemplatesForStage,
} from "@/lib/messageTemplatesApi";

const STATUS_VALUES = new Set([
  "new", "contacted", "meeting_scheduled",
  "dossier_pending", "dossier", "call_done",
  "in_followup", "villa_on_hold",
  "reservation_sent", "reserved",
  "contract_signed", "closed_won", "lost",
]);

// Backend requires `stage` to be one of the canonical status values. Legacy
// or missing statuses fall back to "new" so the log never 400s.
function stageFor(lead) {
  const s = normalizeStatus(lead?.status);
  return STATUS_VALUES.has(s) ? s : "new";
}

// event_type differentiates the wa.me deep-link opening (informational —
// "prepared") from the operator explicitly confirming the send from the
// action bar ("sent_confirmed"). Only sent_confirmed advances the
// commercial cadence on the backend (last_message_* + next_action_*).
async function logMessageSent(lead, preview, templateKeyForLog, linkedChecklistKey, eventType) {
  try {
    await api.post(`/leads/${lead.lead_id}/messages`, {
      template_key: templateKeyForLog,
      channel: "whatsapp",
      stage: stageFor(lead),
      preview: preview.slice(0, 120),
      linked_checklist_key: linkedChecklistKey || null,
      event_type: eventType || "prepared",
    });
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e };
  }
}

function buildInitialStatuses(withPhone, invalidLeads) {
  const map = {};
  for (const l of withPhone) map[l.lead_id] = "pending";
  for (const l of invalidLeads) map[l.lead_id] = "invalid_phone";
  return map;
}

// Resolve a persisted queue `templateKey` (legacy or backend) against the
// live backend catalog. Direct template_id match first, then seed_key
// migration for legacy Bulk keys ("first_contact" → "whatsapp.first_contact").
function resolveTemplateFromKey(templates, key) {
  if (!key || !Array.isArray(templates)) return null;
  const direct = templates.find((t) => t.template_id === key);
  if (direct) return direct;
  const seedGuess = `whatsapp.${key}`;
  const bySeed = templates.find((t) => t.seed_key === seedGuess);
  if (bySeed) return bySeed;
  return null;
}

export default function BulkWhatsappModal({ leads = [], onClose }) {
  // Split the selection into "usable" and "excluded" once so the UI shows
  // meaningful counters without recomputing on every keystroke.
  const { withPhone, withoutPhone, archivedSkipped } = useMemo(() => {
    const wp = [];
    const np = [];
    const arch = [];
    for (const l of leads) {
      if (l?.archived) { arch.push(l); continue; }
      if (isValidWhatsappPhone(getLeadPhone(l))) wp.push(l);
      else np.push(l);
    }
    return { withPhone: wp, withoutPhone: np, archivedSkipped: arch };
  }, [leads]);

  const invalidLeads = useMemo(
    () => [...withoutPhone, ...archivedSkipped],
    [withoutPhone, archivedSkipped],
  );

  // Backend catalog. Refetched on every modal mount so admin edits show up
  // as soon as the operator reopens.
  const { templates, loading: templatesLoading, error: templatesError, backendEmpty, fallbackUsed, reload } =
    useMessageTemplates({ channel: "whatsapp" });

  // Stage cohort of the current selection — homogeneous vs mixed decides
  // whether we auto-preselect the default of the common stage or leave the
  // choice manual with an explicit banner.
  const stagesInSelection = useMemo(() => {
    const set = new Set();
    for (const l of withPhone) set.add(stageFor(l));
    return set;
  }, [withPhone]);
  const commonStage = stagesInSelection.size === 1 ? [...stagesInSelection][0] : null;
  const stagesLabel = useMemo(() => {
    if (!commonStage) return "";
    return statusLabel(commonStage) || commonStage;
  }, [commonStage]);

  // ------------------------------------------------------------------
  // Queue state
  // ------------------------------------------------------------------
  const currentIds = useMemo(() => leads.map((l) => l.lead_id).sort(), [leads]);
  const [resumeCandidate, setResumeCandidate] = useState(() => {
    const snap = loadQueueSnapshot();
    if (!snap) return null;
    const storedIds = Array.isArray(snap.leadIds) ? [...snap.leadIds].sort() : [];
    return sameLeadSet(storedIds, currentIds) ? snap : null;
  });

  // Editor state. `templateKey` is intentionally a string across both modes:
  //   - backend mode → template_id ("tpl_...")
  //   - fallback mode → legacy whatsappTemplates.js key ("first_contact", …)
  // Preserving the same field name keeps the queue snapshot shape stable so
  // colas guardadas antes de PR #104 siguen restaurables.
  const initialEditor = useMemo(() => {
    const last = loadLastEditor();
    if (last && typeof last.templateKey === "string" && typeof last.template === "string") {
      return { templateKey: last.templateKey, template: last.template };
    }
    const body = getTemplateBody(DEFAULT_TEMPLATE_KEY) ?? "";
    return { templateKey: DEFAULT_TEMPLATE_KEY, template: body };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [templateKey, setTemplateKey] = useState(initialEditor.templateKey);
  const [template, setTemplate] = useState(initialEditor.template);
  const [customTemplates, setCustomTemplates] = useState(() => loadCustomTemplates());
  const [statuses, setStatuses] = useState(() => buildInitialStatuses(withPhone, invalidLeads));
  const [cursor, setCursor] = useState(0);
  const [copyToast, setCopyToast] = useState("");
  const [warning, setWarning] = useState("");
  const [overridesVersion, setOverridesVersion] = useState(0);
  const [markSentBusy, setMarkSentBusy] = useState(false);
  const restoredRef = useRef(false);
  const autoPickedRef = useRef(false);
  // Guards a double-click on "Marcar enviado". State drives the disabled
  // attribute for accessibility; ref prevents the second async click
  // firing during the same render cycle before the state has committed.
  const markSentBusyRef = useRef(false);

  // ------------------------------------------------------------------
  // Backend-body sync state (parity with LeadTemplates)
  // ------------------------------------------------------------------
  // templateEdited (state + ref) — flips true once the operator types in
  // the queue's editor. State drives UI; ref lets the sync effect read
  // the freshest value without adding a dep that would fire on every
  // keystroke.
  //
  // loadedBaseBodyRef — snapshot of the backend body last hydrated into
  // `template`. Compared against `backendTemplate.body` on refetch to
  // detect drift caused by admin edits while this modal was open.
  //
  // baseChangedWhileEditing — banner state raised when the shared body
  // drifted AND the operator has a draft in-flight.
  const [templateEdited, setTemplateEdited] = useState(false);
  const templateEditedRef = useRef(false);
  const setTemplateEditedBoth = (v) => { templateEditedRef.current = v; setTemplateEdited(v); };
  const [baseChangedWhileEditing, setBaseChangedWhileEditing] = useState(false);
  const loadedBaseBodyRef = useRef(initialEditor.template || "");

  // Backend-selected template (if any) — the object matching templateKey in
  // backend mode. Null when we're in fallback mode or templateKey doesn't
  // correspond to a backend row.
  const backendTemplate = useMemo(() => {
    if (fallbackUsed) return null;
    return templates.find((t) => t.template_id === templateKey) || null;
  }, [templates, templateKey, fallbackUsed]);

  // ------------------------------------------------------------------
  // Auto-preselection when backend arrives / stage cohort changes
  // ------------------------------------------------------------------
  // Runs at most once per (templates, commonStage) combo. Only overrides
  // templateKey/template when the current selection is invalid in the new
  // catalog. Never fires while the resume prompt is active — the operator
  // decides first.
  useEffect(() => {
    if (fallbackUsed) return;
    if (resumeCandidate) return;
    if (templates.length === 0) return;

    const current = templates.find((t) => t.template_id === templateKey);

    // 1) Legacy key still in `templateKey` — try to migrate via seed_key.
    // Identifier-only fixup: DO NOT touch `template`. The body may be an
    // in-flight operator edit restored from the resume snapshot or from
    // last-editor localStorage, and the whole point of migration is to
    // reconnect that state to the shared catalog without dropping it.
    // Missing body → seed the editor from the migrated template so the
    // textarea doesn't render empty. Mark autoPicked so we don't fall
    // through into the default-picker and re-overwrite.
    if (!current) {
      const migrated = resolveTemplateFromKey(templates, templateKey);
      if (migrated) {
        setTemplateKey(migrated.template_id);
        if (!template) setTemplate(migrated.body || "");
        autoPickedRef.current = true;
        return;
      }
      // No migration path — fall through to auto-pick.
    }

    // Post-autopick: current selection still exists in the fresh catalog.
    // Sync the body against backend so admin edits arriving via reload()
    // reach the textarea when the operator hasn't touched it — but never
    // clobber a draft in flight. Ref-based comparison keeps deps small.
    if (autoPickedRef.current && current) {
      const backendBody = current.body || "";
      if (backendBody !== loadedBaseBodyRef.current) {
        if (!templateEditedRef.current) {
          setTemplate(backendBody);
          loadedBaseBodyRef.current = backendBody;
          setBaseChangedWhileEditing(false);
        } else {
          loadedBaseBodyRef.current = backendBody;
          setBaseChangedWhileEditing(true);
        }
      }
      return;
    }

    // 2) Homogeneous selection → default for the common stage. Mixed →
    //    keep last valid backend choice; if none, pick a global; else the
    //    first available.
    let target = null;
    if (commonStage) {
      target = pickDefaultTemplateForStage(templates, commonStage);
    } else if (current) {
      target = current;
    } else {
      target = pickDefaultTemplateForStage(templates, null);
    }
    if (target) {
      // Only apply when it changes something so we don't overwrite manual
      // edits the operator has already made in the editor.
      if (target.template_id !== templateKey) {
        setTemplateKey(target.template_id);
        setTemplate(target.body || "");
        loadedBaseBodyRef.current = target.body || "";
        setTemplateEditedBoth(false);
        setBaseChangedWhileEditing(false);
      } else if (!loadedBaseBodyRef.current) {
        // First mount where templateKey already matched target — seed the
        // baseline so subsequent drift comparisons work.
        loadedBaseBodyRef.current = target.body || "";
      }
      autoPickedRef.current = true;
    }
  }, [templates, fallbackUsed, resumeCandidate, commonStage, templateKey]);

  // ------------------------------------------------------------------
  // Fallback-only derivations (overrides / custom management)
  // ------------------------------------------------------------------
  const templateBaseBody = useMemo(() => {
    if (!fallbackUsed) return backendTemplate?.body || "";
    const body = getTemplateBody(templateKey);
    if (body != null) return body;
    return "";
  }, [fallbackUsed, backendTemplate, templateKey, customTemplates, overridesVersion]);

  const isDirty = template !== templateBaseBody
    && (fallbackUsed ? templateKey !== "custom" : true);
  const hasOverride = useMemo(
    () => (fallbackUsed ? hasBuiltInOverride(templateKey) : false),
    [fallbackUsed, templateKey, overridesVersion],
  );
  const canSaveBuiltIn = fallbackUsed
    && isBuiltInTemplate(templateKey)
    && templateKey !== "custom"
    && isDirty;

  const variables = whatsappTemplateVariables();

  const currentLead = withPhone[cursor] || null;
  const currentPhone = currentLead ? normalizeWhatsappPhone(getLeadPhone(currentLead)) : "";
  const currentMessage = currentLead ? renderWhatsappTemplate(template, currentLead) : "";
  const currentUrl = currentLead ? buildWhatsappUrl(getLeadPhone(currentLead), currentMessage) : null;
  const currentStatus = currentLead ? (statuses[currentLead.lead_id] || "pending") : null;

  const counts = useMemo(() => {
    const acc = { pending: 0, opened: 0, sent_manual: 0, skipped: 0, invalid_phone: 0 };
    for (const l of withPhone) {
      const s = statuses[l.lead_id] || "pending";
      if (acc[s] != null) acc[s] += 1;
    }
    for (const l of invalidLeads) {
      acc.invalid_phone += 1;
    }
    return acc;
  }, [statuses, withPhone, invalidLeads]);

  const totalUsable = withPhone.length;
  const allDone = totalUsable > 0
    && withPhone.every((l) => {
      const s = statuses[l.lead_id] || "pending";
      return s === "sent_manual" || s === "skipped";
    });

  const isRecommended = !!(backendTemplate
    && commonStage
    && backendTemplate.stage === commonStage
    && backendTemplate.is_default_for_stage);

  // Selector-ordered list (backend mode only). We pass the commonStage so
  // homogeneous cohorts see the default first; in mixed cohorts we pass
  // null and the ordering falls back to globals-then-name.
  const orderedTemplates = useMemo(() => {
    if (fallbackUsed) return [];
    return orderTemplatesForStage(templates, commonStage);
  }, [fallbackUsed, templates, commonStage]);

  // ------------------------------------------------------------------
  // Snapshot persistence — runs after every meaningful change so a
  // reload/app-switch can restore the exact position. The resume prompt
  // (if any) is honoured before the first write.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (resumeCandidate) return;
    if (allDone) {
      clearQueueSnapshot();
      return;
    }
    saveQueueSnapshot({
      leadIds: currentIds,
      currentIndex: cursor,
      statuses,
      template,
      templateKey,
      createdAt: new Date().toISOString(),
    });
  }, [statuses, cursor, template, templateKey, allDone, resumeCandidate, currentIds]);

  useEffect(() => {
    if (resumeCandidate) return;
    saveLastEditor(templateKey, template);
  }, [templateKey, template, resumeCandidate]);

  // ------------------------------------------------------------------
  // Actions
  // ------------------------------------------------------------------
  const setLeadStatus = (leadId, status) => {
    setStatuses((prev) => ({ ...prev, [leadId]: status }));
  };

  const advance = () => {
    setCursor((prev) => {
      const start = prev + 1;
      for (let i = start; i < withPhone.length; i += 1) {
        const s = statuses[withPhone[i].lead_id] || "pending";
        if (s === "pending" || s === "opened") return i;
      }
      for (let i = 0; i <= prev; i += 1) {
        const s = statuses[withPhone[i].lead_id] || "pending";
        if (s === "pending" || s === "opened") return i;
      }
      return prev;
    });
  };

  // The template_key we hand off to POST /leads/{id}/messages. In backend
  // mode it's the shared template_id; in fallback we keep the historical
  // BULK_TEMPLATE_KEY so analytics can still segment the semi-manual
  // Bulk flow from ad-hoc lead composer sends.
  const templateKeyForLog = () => {
    if (!fallbackUsed && backendTemplate) return backendTemplate.template_id;
    return BULK_TEMPLATE_KEY;
  };
  const linkedChecklistKeyForLog = () => {
    if (!fallbackUsed && backendTemplate) return backendTemplate.linked_checklist_key || null;
    return null;
  };

  const openWhatsapp = async () => {
    if (!currentLead || !currentUrl) return;
    setWarning("");
    const win = window.open(currentUrl, "_blank", "noopener,noreferrer");
    if (!win) {
      setWarning("Tu navegador bloqueó la ventana. Habilita popups para wa.me y vuelve a intentarlo.");
      return;
    }
    setLeadStatus(currentLead.lead_id, "opened");
    // "prepared" — informational log. Never programmes a follow-up so the
    // operator has to explicitly confirm with "Marcar enviado".
    const res = await logMessageSent(currentLead, currentMessage, templateKeyForLog(), linkedChecklistKeyForLog(), "prepared");
    if (!res.ok) {
      setWarning("WhatsApp abierto, pero no se pudo registrar la actividad. El timeline puede quedar desactualizado.");
    }
  };

  const markSent = async () => {
    if (!currentLead || markSentBusyRef.current) return;
    markSentBusyRef.current = true;
    setMarkSentBusy(true); setWarning("");
    // "sent_confirmed" — the backend advances last_message_* and computes
    // next_action_at from the follow-up rules for the lead's stage. We
    // await the response before flipping the local status so a failed
    // POST leaves the cola exactly where it was: same cursor, same status,
    // no phantom "sent" tick.
    const res = await logMessageSent(
      currentLead, currentMessage, templateKeyForLog(),
      linkedChecklistKeyForLog(), "sent_confirmed",
    );
    if (!res.ok) {
      setWarning("No se pudo confirmar el envío en el servidor. Reinténtalo — el lead sigue pendiente.");
      markSentBusyRef.current = false;
      setMarkSentBusy(false);
      return;
    }
    setLeadStatus(currentLead.lead_id, "sent_manual");
    advance();
    markSentBusyRef.current = false;
    setMarkSentBusy(false);
  };

  const skipCurrent = () => {
    if (!currentLead) return;
    setLeadStatus(currentLead.lead_id, "skipped");
    advance();
  };

  const goPrev = () => setCursor((prev) => Math.max(0, prev - 1));
  const goNext = () => setCursor((prev) => Math.min(withPhone.length - 1, prev + 1));

  const copyToClipboard = async (text, message = "Copiado") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyToast(message);
      setTimeout(() => setCopyToast(""), 1800);
    } catch {
      setWarning("No se pudo copiar al portapapeles.");
    }
  };

  const copyAllUrls = () => {
    if (!withPhone.length) return;
    const lines = withPhone
      .map((l) => {
        const msg = renderWhatsappTemplate(template, l);
        const url = buildWhatsappUrl(getLeadPhone(l), msg);
        return url ? `${l.name || l.lead_id}: ${url}` : null;
      })
      .filter(Boolean)
      .join("\n");
    copyToClipboard(lines, "Enlaces copiados");
  };
  const copyCurrentUrl = () => currentUrl && copyToClipboard(currentUrl, "Enlace copiado");
  const copyCurrentMessage = () => currentMessage && copyToClipboard(currentMessage, "Mensaje copiado");

  // ------------------------------------------------------------------
  // Resume flow
  // ------------------------------------------------------------------
  const applyResume = () => {
    if (!resumeCandidate || restoredRef.current) return;
    restoredRef.current = true;
    const merged = { ...buildInitialStatuses(withPhone, invalidLeads) };
    if (resumeCandidate.statuses && typeof resumeCandidate.statuses === "object") {
      for (const [k, v] of Object.entries(resumeCandidate.statuses)) {
        if (merged[k] === "invalid_phone") continue;
        if (typeof v === "string") merged[k] = v;
      }
    }
    setStatuses(merged);
    if (typeof resumeCandidate.currentIndex === "number") {
      setCursor(Math.max(0, Math.min(withPhone.length - 1, resumeCandidate.currentIndex)));
    }
    // templateKey may be legacy (fallback / pre-PR104) or backend
    // template_id. In backend mode the auto-pick effect below will resolve
    // it via resolveTemplateFromKey once templates are loaded.
    if (typeof resumeCandidate.templateKey === "string") {
      setTemplateKey(resumeCandidate.templateKey);
    }
    if (typeof resumeCandidate.template === "string") setTemplate(resumeCandidate.template);
    setResumeCandidate(null);
  };
  const discardResume = () => {
    setResumeCandidate(null);
    clearQueueSnapshot();
    setStatuses(buildInitialStatuses(withPhone, invalidLeads));
    setCursor(0);
  };

  const restartQueue = () => {
    clearQueueSnapshot();
    setStatuses(buildInitialStatuses(withPhone, invalidLeads));
    setCursor(0);
    setWarning("");
  };

  // ------------------------------------------------------------------
  // Template handlers
  // ------------------------------------------------------------------
  const handleChangeTemplate = (nextKey) => {
    if (nextKey === templateKey) return;
    if (isDirty) {
      const ok = window.confirm("Perderás tus ediciones actuales. ¿Cambiar de plantilla?");
      if (!ok) return;
    }
    if (fallbackUsed) {
      const body = getTemplateBody(nextKey);
      setTemplateKey(nextKey);
      setTemplate(body != null ? body : "");
      return;
    }
    const tpl = templates.find((t) => t.template_id === nextKey);
    if (!tpl) return;
    setTemplateKey(tpl.template_id);
    setTemplate(tpl.body || "");
    loadedBaseBodyRef.current = tpl.body || "";
    setTemplateEditedBoth(false);
    setBaseChangedWhileEditing(false);
  };

  const handleRestoreTemplate = () => {
    if (fallbackUsed) {
      const body = getTemplateBody(templateKey);
      if (body != null) setTemplate(body);
      return;
    }
    if (backendTemplate) {
      setTemplate(backendTemplate.body || "");
      loadedBaseBodyRef.current = backendTemplate.body || "";
      setTemplateEditedBoth(false);
      setBaseChangedWhileEditing(false);
    }
  };

  // Adopt the fresh backend body when admin edited it while the queue's
  // editor had a draft. Clears the draft flag and dismisses the notice.
  const handleAdoptNewBase = () => {
    if (!backendTemplate) return;
    setTemplate(backendTemplate.body || "");
    loadedBaseBodyRef.current = backendTemplate.body || "";
    setTemplateEditedBoth(false);
    setBaseChangedWhileEditing(false);
  };

  // Keep the operator's queue draft as-is; just hide the notice.
  const handleKeepDraft = () => {
    setBaseChangedWhileEditing(false);
  };

  // Manual refetch — fires the shared hook's reload so the operator can
  // pull admin's latest edits without closing the modal. Queue state
  // (statuses, cursor, snapshot) is untouched; the sync effect above
  // decides whether to refresh the body or raise the notice.
  const handleReloadTemplates = () => {
    setWarning("");
    reload();
  };

  // Legacy localStorage-only actions. Only visible when fallbackUsed=true.
  const handleSaveBuiltInEdit = () => {
    if (!canSaveBuiltIn) return;
    const ok = saveBuiltInOverride(templateKey, template);
    if (!ok) {
      setWarning("No se pudieron guardar los cambios (localStorage bloqueado).");
      return;
    }
    setOverridesVersion((v) => v + 1);
    setWarning("");
  };

  const handleRestoreOriginal = () => {
    if (!hasOverride) return;
    const label = WHATSAPP_TEMPLATES.find((t) => t.key === templateKey)?.label || templateKey;
    const confirmed = window.confirm(
      `¿Restaurar el texto original de "${label}"? Se descartará la versión personalizada.`,
    );
    if (!confirmed) return;
    const ok = deleteBuiltInOverride(templateKey);
    if (!ok) {
      setWarning("No se pudo restaurar el original.");
      return;
    }
    const original = WHATSAPP_TEMPLATES.find((t) => t.key === templateKey)?.body ?? "";
    setTemplate(original);
    setOverridesVersion((v) => v + 1);
    setWarning("");
  };

  const handleSaveAsTemplate = () => {
    const name = window.prompt("Nombre para la plantilla local:");
    if (name === null) return;
    const trimmed = String(name).trim();
    if (!trimmed) {
      setWarning("El nombre de la plantilla no puede estar vacío.");
      return;
    }
    if (customTemplates.length >= MAX_CUSTOM_TEMPLATES) {
      setWarning(`Máximo ${MAX_CUSTOM_TEMPLATES} plantillas personalizadas. Elimina alguna antes de guardar otra.`);
      return;
    }
    const created = saveCustomTemplate(trimmed, template);
    if (!created) {
      setWarning("No se pudo guardar la plantilla (localStorage bloqueado o cap alcanzado).");
      return;
    }
    setCustomTemplates(loadCustomTemplates());
    setTemplateKey(created.key);
    setWarning("");
  };

  const handleDeleteCustom = (key) => {
    if (isBuiltInTemplate(key)) return;
    const target = customTemplates.find((t) => t.key === key);
    const label = target?.label || "esta plantilla";
    if (!window.confirm(`¿Eliminar la plantilla "${label}"?`)) return;
    const ok = deleteCustomTemplate(key);
    if (!ok) {
      setWarning("No se pudo eliminar la plantilla.");
      return;
    }
    const next = loadCustomTemplates();
    setCustomTemplates(next);
    if (templateKey === key) {
      const body = getTemplateBody(DEFAULT_TEMPLATE_KEY) ?? "";
      setTemplateKey(DEFAULT_TEMPLATE_KEY);
      setTemplate(body);
    }
  };

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  const fallbackBannerText = templatesError
    ? "No se pudieron cargar las plantillas compartidas. Se muestran versiones locales; los cambios no se guardarán para el equipo."
    : backendEmpty
      ? "No hay plantillas compartidas activas. Se muestran versiones locales. Un administrador debe inicializar el catálogo desde Admin → Plantillas de mensajes."
      : null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center sm:p-4"
      onClick={onClose}
      data-testid="bulk-whatsapp-modal"
    >
      <div
        className="bg-white shadow-xl w-full h-full sm:h-auto sm:max-h-[92vh] sm:max-w-2xl sm:rounded-lg flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-stone-200 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <WhatsappLogo size={20} weight="fill" className="text-emerald-600 shrink-0" />
            <div className="font-serif-dv text-lg sm:text-xl truncate">Enviar WhatsApp a leads</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="text-stone-400 hover:text-stone-700 p-2 -mr-2"
            data-testid="bulk-whatsapp-close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
          {resumeCandidate ? (
            <div
              className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-4 space-y-3"
              data-testid="bulk-whatsapp-resume-prompt"
            >
              <div className="text-sm text-emerald-900 font-medium">
                Tienes una cola de WhatsApp pendiente. ¿Continuar?
              </div>
              <div className="text-xs text-emerald-800">
                Guardada el {new Date(resumeCandidate.createdAt).toLocaleString()}.
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={applyResume}
                  className="dv-btn dv-btn-primary text-sm"
                  data-testid="bulk-whatsapp-resume-continue"
                >
                  Continuar
                </button>
                <button
                  type="button"
                  onClick={discardResume}
                  className="dv-btn dv-btn-ghost text-sm"
                  data-testid="bulk-whatsapp-resume-restart"
                >
                  Empezar de nuevo
                </button>
              </div>
            </div>
          ) : null}

          {/* Fallback banner (backend error or empty). Kept above counters
              so operators see it before they start editing. */}
          {fallbackBannerText && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2"
                 data-testid="bulk-whatsapp-fallback-banner">
              <div className="flex-1">{fallbackBannerText}</div>
              <button type="button" onClick={reload}
                      className="text-[10px] uppercase tracking-widest text-amber-900 underline hover:no-underline">
                Reintentar
              </button>
            </div>
          )}

          {/* Mixed-stage warning — only relevant in backend mode with a
              live catalog. In fallback the operator picks manually anyway. */}
          {!fallbackUsed && stagesInSelection.size > 1 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                 data-testid="bulk-whatsapp-mixed-stages">
              Los leads seleccionados pertenecen a distintas fases. Elige una plantilla común para este envío.
            </div>
          )}

          {/* Counters */}
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            <Counter label="Seleccionados" value={leads.length} tone="stone" testid="bulk-count-selected" />
            <Counter label="Con teléfono válido" value={withPhone.length} tone="emerald" testid="bulk-count-valid" />
            <Counter
              label="Sin teléfono válido"
              value={withoutPhone.length + archivedSkipped.length}
              tone="amber"
              testid="bulk-count-invalid"
            />
          </div>
          {archivedSkipped.length > 0 && (
            <div className="text-[11px] text-stone-500">
              {archivedSkipped.length} lead{archivedSkipped.length === 1 ? "" : "s"} archivado{archivedSkipped.length === 1 ? "" : "s"} excluido{archivedSkipped.length === 1 ? "" : "s"} del envío.
            </div>
          )}

          {/* Template selector + editor */}
          <div className="space-y-2" data-testid="bulk-whatsapp-editor">
            <label className="block">
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="text-[11px] uppercase tracking-widest text-stone-500">Plantilla</div>
                <div className="flex items-center gap-2">
                  {isRecommended && (
                    <span className="text-[10px] uppercase tracking-widest text-emerald-700"
                          data-testid="bulk-whatsapp-recommended-flag">
                      Recomendada para la fase {stagesLabel}
                    </span>
                  )}
                  {!fallbackUsed && (
                    <button type="button" onClick={handleReloadTemplates} disabled={templatesLoading}
                            className="text-[10px] uppercase tracking-widest text-stone-500 hover:text-stone-900 inline-flex items-center gap-1 disabled:opacity-40"
                            data-testid="bulk-whatsapp-reload-templates"
                            title="Vuelve a leer las plantillas del backend por si Admin las editó">
                      <ArrowClockwise size={11} /> Recargar
                    </button>
                  )}
                </div>
              </div>
              {fallbackUsed ? (
                <select
                  className="dv-input w-full text-sm"
                  value={templateKey}
                  onChange={(e) => handleChangeTemplate(e.target.value)}
                  data-testid="bulk-whatsapp-template-key"
                >
                  <optgroup label="Plantillas locales">
                    {WHATSAPP_TEMPLATES.filter((t) => t.key !== "custom").map((t) => (
                      <option key={t.key} value={t.key}>{t.label}</option>
                    ))}
                  </optgroup>
                  {customTemplates.length > 0 && (
                    <optgroup label="Personalizadas locales">
                      {customTemplates.map((t) => (
                        <option key={t.key} value={t.key}>{t.label}</option>
                      ))}
                    </optgroup>
                  )}
                  <option value="custom">Mensaje personalizado</option>
                </select>
              ) : (
                <select
                  className="dv-input w-full text-sm"
                  value={templateKey}
                  onChange={(e) => handleChangeTemplate(e.target.value)}
                  data-testid="bulk-whatsapp-template-key"
                >
                  {orderedTemplates.map((t) => {
                    const stageMatch = commonStage && t.stage === commonStage;
                    const suffix = t.is_default_for_stage && stageMatch ? " · Recomendada"
                      : t.stage == null ? " · Global"
                      : stageMatch ? ""
                      : ` · ${statusLabel(t.stage) || t.stage || ""}`;
                    return (
                      <option key={t.template_id} value={t.template_id}>{t.name}{suffix}</option>
                    );
                  })}
                </select>
              )}
            </label>

            {baseChangedWhileEditing && (
              <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 space-y-2"
                   data-testid="bulk-whatsapp-base-changed">
                <div>La plantilla compartida ha cambiado mientras editabas esta cola. Tu mensaje no se ha reemplazado.</div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={handleAdoptNewBase}
                          className="text-[11px] px-2 py-1 rounded border border-amber-300 text-amber-900 hover:border-amber-500"
                          data-testid="bulk-whatsapp-use-new-base">
                    Usar nueva versión
                  </button>
                  <button type="button" onClick={handleKeepDraft}
                          className="text-[11px] px-2 py-1 rounded border border-amber-300 text-amber-900 hover:border-amber-500"
                          data-testid="bulk-whatsapp-keep-draft">
                    Mantener mi mensaje
                  </button>
                </div>
              </div>
            )}

            <label className="block">
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="text-[11px] uppercase tracking-widest text-stone-500">
                  Mensaje base con variables
                </div>
                <div className="flex items-center gap-2">
                  {fallbackUsed && hasOverride && (
                    <span
                      className="text-[10px] uppercase tracking-widest text-emerald-700"
                      data-testid="bulk-whatsapp-override-flag"
                    >
                      Versión personalizada
                    </span>
                  )}
                  {isDirty && (
                    <span className="text-[10px] uppercase tracking-widest text-amber-700" data-testid="bulk-whatsapp-dirty-flag">
                      Modificado
                    </span>
                  )}
                </div>
              </div>
              <textarea
                className="dv-input w-full font-mono text-sm"
                rows={6}
                value={template}
                onChange={(e) => { setTemplate(e.target.value); if (!fallbackUsed) setTemplateEditedBoth(true); }}
                data-testid="bulk-whatsapp-template"
              />
              <div className="text-[11px] text-stone-500 mt-1">
                {fallbackUsed
                  ? <>Las variables como {"{nombre}"} se personalizan por lead. Disponibles: {variables.map((v) => `{${v}}`).join(", ")}.</>
                  : <>Los cambios realizados aquí solo afectan a este envío. Para editar la plantilla compartida, utiliza <span className="font-medium">Admin → Plantillas de mensajes</span>.</>}
              </div>
            </label>

            <div className="flex flex-wrap gap-2">
              {isDirty && (fallbackUsed ? templateKey !== "custom" : true) && (
                <button
                  type="button"
                  onClick={handleRestoreTemplate}
                  className="dv-btn dv-btn-ghost text-xs inline-flex items-center gap-1.5"
                  data-testid="bulk-whatsapp-restore-template"
                >
                  <ArrowCounterClockwise size={12} /> Restaurar mensaje
                </button>
              )}
              {/* Legacy localStorage-only controls. Hidden when backend is
                  live so operators can't accidentally shard shared state. */}
              {fallbackUsed && canSaveBuiltIn && (
                <button
                  type="button"
                  onClick={handleSaveBuiltInEdit}
                  className="dv-btn dv-btn-ghost text-xs inline-flex items-center gap-1.5"
                  data-testid="bulk-whatsapp-save-builtin-edit"
                  title="Guardar los cambios como versión personalizada local de esta plantilla"
                >
                  <FloppyDisk size={12} /> Guardar cambios locales
                </button>
              )}
              {fallbackUsed && hasOverride && templateKey !== "custom" && (
                <button
                  type="button"
                  onClick={handleRestoreOriginal}
                  className="dv-btn dv-btn-ghost text-xs inline-flex items-center gap-1.5"
                  data-testid="bulk-whatsapp-restore-original"
                  title="Descartar la versión local y volver al texto original"
                >
                  <ArrowClockwise size={12} /> Restaurar original local
                </button>
              )}
              {fallbackUsed && (
                <button
                  type="button"
                  onClick={handleSaveAsTemplate}
                  disabled={!template.trim() || customTemplates.length >= MAX_CUSTOM_TEMPLATES}
                  className="dv-btn dv-btn-ghost text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
                  data-testid="bulk-whatsapp-save-template"
                  title={customTemplates.length >= MAX_CUSTOM_TEMPLATES ? `Máximo ${MAX_CUSTOM_TEMPLATES} plantillas personalizadas` : "Guardar el texto actual como plantilla local"}
                >
                  <FloppyDisk size={12} /> Guardar como plantilla local
                </button>
              )}
            </div>

            {fallbackUsed && customTemplates.length > 0 && (
              <details className="text-xs text-stone-600" data-testid="bulk-whatsapp-manage-customs">
                <summary className="cursor-pointer text-stone-500">
                  Gestionar plantillas locales ({customTemplates.length}/{MAX_CUSTOM_TEMPLATES})
                </summary>
                <ul className="mt-2 space-y-1">
                  {customTemplates.map((t) => (
                    <li key={t.key} className="flex items-center justify-between gap-2 px-2 py-1 rounded bg-stone-50 border border-stone-100">
                      <span className="truncate">{t.label}</span>
                      <button
                        type="button"
                        onClick={() => handleDeleteCustom(t.key)}
                        aria-label={`Eliminar plantilla ${t.label}`}
                        className="text-stone-400 hover:text-red-600 p-1"
                        data-testid={`bulk-whatsapp-delete-custom-${t.key}`}
                      >
                        <Trash size={12} />
                      </button>
                    </li>
                  ))}
                </ul>
              </details>
            )}

            {templatesLoading && (
              <div className="text-[11px] text-stone-500" data-testid="bulk-whatsapp-templates-loading">
                Cargando plantillas compartidas…
              </div>
            )}
          </div>

          {/* Queue / preview / summary */}
          {withPhone.length === 0 ? (
            <div className="rounded-md border border-stone-200 bg-stone-50 px-4 py-6 text-center text-sm text-stone-600" data-testid="bulk-whatsapp-empty">
              No hay leads con teléfono válido en la selección.
            </div>
          ) : allDone ? (
            <div
              className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-5 text-sm text-emerald-900 space-y-3"
              data-testid="bulk-whatsapp-done"
            >
              <div className="flex items-center gap-2 font-medium">
                <CheckCircle size={20} weight="fill" /> Todos los WhatsApp han sido gestionados.
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <SummaryCell label="Enviados" value={counts.sent_manual} tone="emerald" testid="bulk-summary-sent" />
                <SummaryCell label="Saltados" value={counts.skipped} tone="stone" testid="bulk-summary-skipped" />
                <SummaryCell label="Inválidos" value={counts.invalid_phone} tone="amber" testid="bulk-summary-invalid" />
                <SummaryCell label="Total" value={leads.length} tone="stone" testid="bulk-summary-total" />
              </div>
              <button
                type="button"
                onClick={restartQueue}
                className="dv-btn dv-btn-ghost text-sm inline-flex items-center gap-1.5"
                data-testid="bulk-whatsapp-restart"
              >
                <ArrowClockwise size={14} /> Empezar cola nueva con esta selección
              </button>
            </div>
          ) : (
            <div className="rounded-md border border-stone-200 bg-white" data-testid="bulk-whatsapp-preview">
              <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-stone-200 bg-stone-50 text-xs flex-wrap">
                <span className="text-stone-600">
                  Lead {Math.min(cursor + 1, withPhone.length)} de {withPhone.length}
                </span>
                <div className="flex items-center gap-2 text-stone-500">
                  <StatusChip status={currentStatus} />
                  <span>
                    ✓{counts.sent_manual} · ⤵{counts.skipped} · ◐{counts.opened} · ⋯{counts.pending}
                  </span>
                </div>
              </div>
              {currentLead && (
                <div className="p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <div className="font-medium truncate">{currentLead.name || currentLead.lead_id}</div>
                    <div className="text-xs text-stone-500 shrink-0">wa.me/{currentPhone}</div>
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-500">
                    Vista previa para {currentLead.name || currentLead.lead_id}
                  </div>
                  <div
                    className="text-xs text-stone-700 whitespace-pre-wrap bg-stone-50 rounded p-2 border border-stone-100"
                    data-testid="bulk-whatsapp-preview-body"
                  >
                    {currentMessage}
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <button
                      type="button"
                      onClick={goPrev}
                      disabled={cursor === 0}
                      className="dv-btn dv-btn-ghost text-xs inline-flex items-center gap-1 disabled:opacity-40"
                      data-testid="bulk-whatsapp-prev"
                    >
                      <ArrowLeft size={12} /> Anterior
                    </button>
                    <button
                      type="button"
                      onClick={goNext}
                      disabled={cursor >= withPhone.length - 1}
                      className="dv-btn dv-btn-ghost text-xs inline-flex items-center gap-1 disabled:opacity-40"
                      data-testid="bulk-whatsapp-next"
                    >
                      Siguiente <ArrowRight size={12} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {warning && (
            <div className="text-xs px-3 py-2 rounded border bg-amber-50 border-amber-200 text-amber-800" data-testid="bulk-whatsapp-warning">
              {warning}
            </div>
          )}
          {copyToast && (
            <div className="text-xs px-3 py-2 rounded border bg-stone-100 border-stone-200 text-stone-700" data-testid="bulk-whatsapp-copy-toast">
              {copyToast}
            </div>
          )}

          {withPhone.length > 0 && !allDone && (
            <div className="flex flex-wrap gap-2 text-sm">
              <button
                type="button"
                onClick={copyCurrentMessage}
                disabled={!currentMessage}
                className="dv-btn dv-btn-ghost inline-flex items-center gap-1.5"
                data-testid="bulk-whatsapp-copy-message"
                title="Copia el mensaje renderizado del lead actual"
              >
                <ClipboardText size={14} /> Copiar mensaje
              </button>
              <button
                type="button"
                onClick={copyCurrentUrl}
                disabled={!currentUrl}
                className="dv-btn dv-btn-ghost inline-flex items-center gap-1.5"
                data-testid="bulk-whatsapp-copy-current"
                title="Copia el enlace del lead actual"
              >
                <Copy size={14} /> Copiar enlace
              </button>
              <button
                type="button"
                onClick={copyAllUrls}
                className="dv-btn dv-btn-ghost inline-flex items-center gap-1.5"
                data-testid="bulk-whatsapp-copy-all"
                title="Copia todos los enlaces wa.me al portapapeles"
              >
                <Copy size={14} /> Copiar enlaces
              </button>
            </div>
          )}

          {(withoutPhone.length + archivedSkipped.length) > 0 && (
            <details className="text-xs text-stone-600">
              <summary className="cursor-pointer text-stone-500">
                Ver {withoutPhone.length + archivedSkipped.length} lead{(withoutPhone.length + archivedSkipped.length) === 1 ? "" : "s"} excluido{(withoutPhone.length + archivedSkipped.length) === 1 ? "" : "s"}
              </summary>
              <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                {withoutPhone.map((l) => (
                  <li key={l.lead_id} className="flex justify-between gap-2">
                    <span className="truncate">{l.name || l.lead_id}</span>
                    <span className="text-stone-400 text-[11px]">sin teléfono válido</span>
                  </li>
                ))}
                {archivedSkipped.map((l) => (
                  <li key={l.lead_id} className="flex justify-between gap-2">
                    <span className="truncate">{l.name || l.lead_id}</span>
                    <span className="text-stone-400 text-[11px]">archivado</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>

        {!allDone && withPhone.length > 0 && !resumeCandidate && (
          <div
            className="shrink-0 border-t border-stone-200 bg-white px-3 sm:px-6 py-3 grid grid-cols-3 gap-2 sm:flex sm:justify-end sm:gap-3"
            data-testid="bulk-whatsapp-action-bar"
            style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
          >
            <button
              type="button"
              onClick={openWhatsapp}
              disabled={!currentLead || !currentUrl}
              className="dv-btn dv-btn-primary text-sm inline-flex items-center justify-center gap-1.5 min-h-[44px]"
              style={{ background: "#25D366", color: "#fff", opacity: (!currentLead || !currentUrl) ? 0.4 : 1 }}
              data-testid="bulk-whatsapp-open"
            >
              <WhatsappLogo size={18} weight="fill" /> Abrir WhatsApp
            </button>
            <button
              type="button"
              onClick={markSent}
              disabled={!currentLead || markSentBusy}
              className="dv-btn dv-btn-primary text-sm inline-flex items-center justify-center gap-1.5 min-h-[44px] bg-stone-900 text-white disabled:opacity-40"
              data-testid="bulk-whatsapp-mark-sent"
            >
              <Check size={16} weight="bold" /> {markSentBusy ? "Enviando…" : "Marcar enviado"}
            </button>
            <button
              type="button"
              onClick={skipCurrent}
              disabled={!currentLead}
              className="dv-btn dv-btn-ghost text-sm inline-flex items-center justify-center gap-1.5 min-h-[44px] disabled:opacity-40"
              data-testid="bulk-whatsapp-skip"
            >
              <SkipForward size={16} weight="regular" /> Saltar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Presentational helpers
// ---------------------------------------------------------------------------
function Counter({ label, value, tone = "stone", testid }) {
  const toneMap = {
    stone: "bg-stone-100 text-stone-700 border-stone-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
  };
  return (
    <div className={`rounded border px-2.5 sm:px-3 py-2 ${toneMap[tone] || toneMap.stone}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest opacity-70 leading-tight">{label}</div>
      <div className="font-serif-dv text-xl sm:text-2xl leading-none mt-1">{value}</div>
    </div>
  );
}

function SummaryCell({ label, value, tone = "stone", testid }) {
  const toneMap = {
    stone: "text-stone-700",
    emerald: "text-emerald-800",
    amber: "text-amber-800",
  };
  return (
    <div className="rounded bg-white/60 border border-emerald-100 px-2 py-1.5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
      <div className={`font-serif-dv text-lg leading-none mt-0.5 ${toneMap[tone] || toneMap.stone}`}>{value}</div>
    </div>
  );
}

const STATUS_CHIP_MAP = {
  pending:       { label: "Pendiente",     className: "bg-stone-100 text-stone-700 border-stone-200" },
  opened:        { label: "WhatsApp abierto", className: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  sent_manual:   { label: "Enviado",       className: "bg-emerald-100 text-emerald-900 border-emerald-300" },
  skipped:       { label: "Saltado",       className: "bg-stone-200 text-stone-700 border-stone-300" },
  invalid_phone: { label: "Sin teléfono",  className: "bg-amber-50 text-amber-800 border-amber-200" },
};

function StatusChip({ status }) {
  const conf = STATUS_CHIP_MAP[status] || STATUS_CHIP_MAP.pending;
  return (
    <span
      className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${conf.className}`}
      data-testid={`bulk-whatsapp-status-chip-${status}`}
    >
      {conf.label}
    </span>
  );
}
